import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Edit3, X, CheckCircle2, AlertCircle, ExternalLink, Award } from "lucide-react";
import { caseStudiesApi } from "../lib/api";

const empty = {
  title: "",
  slug: "",
  client_display_name: "",
  company: "",
  industry: "",
  standard: "",
  duration: "",
  challenge: "",
  solution: "",
  outcome_summary: "",
  outcomes: [],
  quote: "",
  quote_author: "",
  quote_role: "",
  image: "",
  published: false,
  consent_confirmed: false,
};

const CaseStudiesCMS = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // slug being edited
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = () => { setLoading(true); caseStudiesApi.listAll().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const addOutcome = () => setForm({ ...form, outcomes: [...form.outcomes, { label: "", value: "" }] });
  const setOutcome = (i, k, v) => {
    const list = [...form.outcomes];
    list[i] = { ...list[i], [k]: v };
    setForm({ ...form, outcomes: list });
  };
  const rmOutcome = (i) => setForm({ ...form, outcomes: form.outcomes.filter((_, x) => x !== i) });

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.published && !form.consent_confirmed) {
      setErr("Please confirm client consent before publishing.");
      return;
    }
    try {
      if (editing) await caseStudiesApi.update(editing, form); else await caseStudiesApi.create(form);
      setEditing(null); setForm(empty); load();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (c) => {
    setEditing(c.slug);
    setForm({
      title: c.title || "",
      slug: c.slug || "",
      client_display_name: c.client_display_name || "",
      company: c.company || "",
      industry: c.industry || "",
      standard: c.standard || "",
      duration: c.duration || "",
      challenge: c.challenge || "",
      solution: c.solution || "",
      outcome_summary: c.outcome_summary || "",
      outcomes: c.outcomes || [],
      quote: c.quote || "",
      quote_author: c.quote_author || "",
      quote_role: c.quote_role || "",
      image: c.image || "",
      published: !!c.published,
      consent_confirmed: !!c.consent_confirmed,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (slug) => { if (window.confirm("Delete this case study?")) { await caseStudiesApi.remove(slug); load(); } };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Case Studies</h2>
        {editing && <button onClick={() => { setEditing(null); setForm(empty); setErr(""); }} className="btn-outline text-sm"><X size={14}/> Cancel edit</button>}
      </div>

      {err && (
        <div className="mt-3 flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 px-3 py-2 rounded-lg">
          <AlertCircle size={14}/> {err}
        </div>
      )}

      <form onSubmit={save} className="bg-white border border-slate-100 rounded-xl p-5 mt-4 grid sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold">Title*</label>
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="contact-input" placeholder="e.g. Tier-1 auto supplier reaches IATF 16949 in 8 months" />
        </div>
        <div>
          <label className="text-xs font-semibold">Standard</label>
          <input value={form.standard} onChange={(e) => setForm({ ...form, standard: e.target.value })} className="contact-input" placeholder="ISO 27001" />
        </div>
        <div>
          <label className="text-xs font-semibold">Industry</label>
          <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="contact-input" placeholder="Automotive" />
        </div>
        <div>
          <label className="text-xs font-semibold">Client (display name)</label>
          <input value={form.client_display_name} onChange={(e) => setForm({ ...form, client_display_name: e.target.value })} className="contact-input" placeholder="A leading tier-1 supplier" />
        </div>
        <div>
          <label className="text-xs font-semibold">Company (optional)</label>
          <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="contact-input" placeholder="ABC Pvt Ltd (leave blank for anonymity)" />
        </div>
        <div>
          <label className="text-xs font-semibold">Duration</label>
          <input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} className="contact-input" placeholder="8 months" />
        </div>
        <div>
          <label className="text-xs font-semibold">Hero Image URL</label>
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="contact-input" placeholder="https://..." />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold">Challenge*</label>
          <textarea required rows={3} value={form.challenge} onChange={(e) => setForm({ ...form, challenge: e.target.value })} className="contact-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold">Our Solution*</label>
          <textarea required rows={3} value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} className="contact-input" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold">Outcome Summary</label>
          <textarea rows={2} value={form.outcome_summary} onChange={(e) => setForm({ ...form, outcome_summary: e.target.value })} className="contact-input" placeholder="One-line summary of the results" />
        </div>

        <div className="sm:col-span-2 border border-slate-100 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">Outcome Metrics (up to 6)</div>
            <button type="button" onClick={addOutcome} className="btn-outline text-xs"><Plus size={12}/> Add metric</button>
          </div>
          {form.outcomes.length === 0 && <div className="text-xs text-slate-500 mt-2">No metrics added.</div>}
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {form.outcomes.map((o, i) => (
              <div key={i} className="flex gap-2">
                <input value={o.label} onChange={(e) => setOutcome(i, "label", e.target.value)} placeholder="Label (e.g. PPM reduction)" className="contact-input text-sm flex-1" />
                <input value={o.value} onChange={(e) => setOutcome(i, "value", e.target.value)} placeholder="Value (e.g. 62%)" className="contact-input text-sm w-32" />
                <button type="button" onClick={() => rmOutcome(i)} className="btn-outline text-sm text-red-600"><Trash2 size={12}/></button>
              </div>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold">Client Quote</label>
          <textarea rows={2} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className="contact-input" />
        </div>
        <div>
          <label className="text-xs font-semibold">Quote Author</label>
          <input value={form.quote_author} onChange={(e) => setForm({ ...form, quote_author: e.target.value })} className="contact-input" placeholder="Head of Quality" />
        </div>
        <div>
          <label className="text-xs font-semibold">Quote Role / Company</label>
          <input value={form.quote_role} onChange={(e) => setForm({ ...form, quote_role: e.target.value })} className="contact-input" placeholder="Tier-1 Auto Supplier" />
        </div>

        <div className="sm:col-span-2 border-t pt-3 mt-2 grid sm:grid-cols-2 gap-3">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={form.consent_confirmed} onChange={(e) => setForm({ ...form, consent_confirmed: e.target.checked })} className="mt-1" />
            <span><b>Client consent confirmed.</b> The client has approved this case study for public publishing including the details, metrics and quote above.</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} />
            <span><b>Publish publicly</b> (visible on /case-studies)</span>
          </label>
        </div>

        <button type="submit" className="btn-brand justify-center sm:col-span-2">
          {editing ? "Update Case Study" : "Save Case Study"} <CheckCircle2 size={14}/>
        </button>
      </form>

      <h3 className="mt-8 font-semibold text-slate-900">All Case Studies ({items.length})</h3>
      {loading ? <Loader2 className="animate-spin mt-4"/> : (
        <div className="mt-3 grid md:grid-cols-2 gap-3">
          {items.length === 0 && <div className="text-slate-500 text-sm">No case studies yet.</div>}
          {items.map((c) => (
            <div key={c.slug} className="bg-white rounded-xl border border-slate-100 p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {c.image ? <img src={c.image} alt="" className="w-16 h-16 object-cover rounded-lg flex-shrink-0"/> : <div className="w-16 h-16 rounded-lg bg-red-50 text-brand flex items-center justify-center flex-shrink-0"><Award size={20}/></div>}
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{c.title}</div>
                  <div className="text-xs text-slate-500 truncate">/{c.slug}  {c.standard}  {c.industry}</div>
                  <div className="mt-1 text-xs">
                    <span className={`inline-block px-2 py-0.5 rounded font-semibold ${c.published ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {c.published ? "Published" : "Draft"}
                    </span>
                    {c.consent_confirmed && <span className="ml-2 text-emerald-700 text-[11px]">Consent</span>}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {c.published && (
                  <a href={`/case-studies/${c.slug}`} target="_blank" rel="noreferrer" className="btn-outline text-sm" title="View"><ExternalLink size={14}/></a>
                )}
                <button onClick={() => edit(c)} className="btn-outline text-sm"><Edit3 size={14}/></button>
                <button onClick={() => del(c.slug)} className="btn-outline text-sm text-red-600 hover:border-red-500"><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CaseStudiesCMS;
